if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mediaNavPrev: this.querySelector('[data-js-media-nav-prev]'),
          mediaNavNext: this.querySelector('[data-js-media-nav-next]'),
          mediaCounterCurrent: this.querySelector('[data-js-media-counter-current]'),
          mediaCounterTotal: this.querySelector('[data-js-media-counter-total]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.elements.mediaNavPrev && this.elements.mediaNavNext) {
          this.elements.mediaNavPrev.addEventListener('click', this.onMediaNavClick.bind(this, -1), true);
          this.elements.mediaNavNext.addEventListener('click', this.onMediaNavClick.bind(this, 1), true);
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.updateMediaNavigationState();
      }

      onSlideChanged(event) {
        if (!event?.detail?.currentElement) return;
        this.getMediaItems().forEach((element) => {
          element.classList.remove('is-active');
        });
        event.detail.currentElement.classList.add('is-active');

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateMediaNavigationState();
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      getActiveMediaIndex(mediaItems) {
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (!activeMedia) return 0;
        const activeIndex = mediaItems.findIndex((mediaItem) => mediaItem.dataset.mediaId === activeMedia.dataset.mediaId);
        return activeIndex >= 0 ? activeIndex : 0;
      }

      getCurrentMediaIndex(mediaItems) {
        const activeIndex = this.getActiveMediaIndex(mediaItems);
        if (!this.elements.viewer.slider) return activeIndex;

        const mediaOffsets = mediaItems.map((mediaItem) => mediaItem.offsetLeft);
        const hasMultipleOffsets = new Set(mediaOffsets).size > 1;
        if (!hasMultipleOffsets) return activeIndex;

        const currentScrollLeft = this.elements.viewer.slider.scrollLeft;
        let nearestIndex = activeIndex;
        let nearestDistance = Infinity;

        mediaItems.forEach((mediaItem, index) => {
          const distance = Math.abs(mediaItem.offsetLeft - currentScrollLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
            return;
          }

          // offsetが同距離の場合は表示中（is-active）を優先
          if (distance === nearestDistance && index === activeIndex) {
            nearestIndex = index;
          }
        });

        return nearestIndex;
      }

      onMediaNavClick(step, event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const currentIndex = this.getCurrentMediaIndex(mediaItems);
        const targetIndex = Math.max(0, Math.min(currentIndex + step, mediaItems.length - 1));
        if (currentIndex === targetIndex) {
          this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
          return;
        }

        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      updateMediaNavigationState() {
        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;

        const currentIndex = this.getActiveMediaIndex(mediaItems);
        this.updateMediaCounter(currentIndex, mediaItems.length);
        if (!this.elements.mediaNavPrev || !this.elements.mediaNavNext) return;

        if (currentIndex <= 0) {
          this.elements.mediaNavPrev.setAttribute('disabled', 'disabled');
          this.elements.mediaNavPrev.setAttribute('aria-disabled', 'true');
        } else {
          this.elements.mediaNavPrev.removeAttribute('disabled');
          this.elements.mediaNavPrev.setAttribute('aria-disabled', 'false');
        }

        if (currentIndex >= mediaItems.length - 1) {
          this.elements.mediaNavNext.setAttribute('disabled', 'disabled');
          this.elements.mediaNavNext.setAttribute('aria-disabled', 'true');
        } else {
          this.elements.mediaNavNext.removeAttribute('disabled');
          this.elements.mediaNavNext.setAttribute('aria-disabled', 'false');
        }
      }

      updateMediaCounter(currentIndex, totalCount) {
        if (this.elements.mediaCounterCurrent) {
          this.elements.mediaCounterCurrent.textContent = `${currentIndex + 1}`;
        }
        if (this.elements.mediaCounterTotal) {
          this.elements.mediaCounterTotal.textContent = `${totalCount}`;
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.getMediaItems().forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
        this.updateMediaNavigationState();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
